import React from 'react';
import { Link } from 'react-router-dom';
import logo from '../../assets/lis-logo.png';
import Icon, { IconName } from '../../components/Icon/Icon';
import './Home.css';

const laboratories: { name: string; location: string; description: string; href?: string; icon: IconName }[] = [
  { name: 'Laboratorio IS', location: 'Salón 18-210', description: 'Enfocado en estudiantes de Ingeniería de Sistemas, con equipos para uso individual y software para sus necesidades académicas.', href: 'https://lisudea.github.io/LIS/', icon: 'monitor' },
  { name: 'Telemática', location: 'Salón 18-325', description: 'Orientado a estudiantes de Sistemas, Telecomunicaciones, Eléctrica y Electrónica.', href: 'https://lisudea.github.io/telematica/', icon: 'network' },
  { name: 'Móvil IS', location: 'Al interior de Telemática', description: 'Espacio para clases, proyectos académicos y monitorías del Departamento de Ingeniería de Sistemas.', icon: 'mobile' },
];

const socialLinks: { id: IconName; title: string; text: string; cta: string; href: string }[] = [
  { id: 'discord', title: 'Únete a nuestro Discord', text: 'Entérate de noticias y eventos del laboratorio.', cta: 'Unirse', href: 'https://discord.gg/cSg9wUJuJC' },
  { id: 'instagram', title: 'Síguenos en Instagram', text: 'Participa en nuestras actividades e iniciativas.', cta: 'Seguir', href: 'https://www.instagram.com/sistemasudea/' },
  { id: 'facebook', title: 'Síguenos en Facebook', text: 'Disfruta del contenido que compartimos.', cta: 'Seguir', href: 'https://www.facebook.com/LISUdeA' },
  { id: 'x', title: 'Síguenos en X', text: 'Mantente al día con las noticias del departamento.', cta: 'Seguir', href: 'https://twitter.com/DsistemasUdeA' },
];

const Arrow = () => <Icon name="arrow-right" className="lis-inline-icon" />;

const Home = () => {
  React.useEffect(() => {
    const elements = Array.from(document.querySelectorAll<HTMLElement>('#home-page [data-reveal]'));
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reducedMotion || !('IntersectionObserver' in window)) {
      elements.forEach((element) => element.classList.add('is-visible'));
      return;
    }

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        entry.target.classList.add('is-visible');
        observer.unobserve(entry.target);
      });
    }, { rootMargin: '0px 0px -8% 0px', threshold: 0.12 });

    elements.forEach((element) => observer.observe(element));
    return () => observer.disconnect();
  }, []);

  return (
  <div id="home-page">
    <section id="inicio" className="home-hero home-anchor">
      <div className="home-hero-orb home-hero-orb--solid" aria-hidden="true" />
      <div className="home-hero-orb home-hero-orb--ring" aria-hidden="true" />
      <div className="lis-container home-hero-grid">
        <div className="home-hero-copy lis-reveal">
          <p className="lis-eyebrow">Laboratorio Integrado de Sistemas</p>
          <h1>Conocimiento<br />que nos conecta</h1>
          <p className="home-hero-lead">Una comunidad donde se crea, se aprende y se comparte conocimiento libremente.</p>
          <div className="home-hero-actions">
            <a href="#laboratorios" className="lis-button lis-button--primary lis-button--large lis-button--mobile-wide">Explorar laboratorios <Arrow /></a>
            <a href="#quienes-somos" className="lis-button lis-button--secondary lis-button--large lis-button--mobile-wide">Conócenos</a>
          </div>
        </div>
        <div className="home-logo-stage" aria-hidden="true">
          <span className="home-logo-ring" />
          <span className="home-logo-disc" />
          <img src={logo} alt="" className="home-logo" />
        </div>
      </div>
    </section>

    <section id="quienes-somos" className="lis-container lis-section about-grid home-anchor reveal-section" data-reveal>
      <div className="lis-section-heading">
        <p className="lis-eyebrow">¿Quiénes somos?</p>
        <h2>Un espacio para intercambiar conocimiento</h2>
      </div>
      <div className="about-copy">
        <p>Somos un espacio donde estudiantes y profesores tienen la posibilidad de intercambiar conocimientos y compartir experiencias formativas. Este espacio está compuesto por el Laboratorio Integrado de Sistemas (LIS), Telemática y Móvil IS.</p>
        <dl className="about-values reveal-section" data-reveal>
          <div><Icon name="users" /><dt>Comunidad</dt><dd>Conecta y participa en nuestras iniciativas.</dd></div>
          <div><Icon name="graduation" /><dt>Formación</dt><dd>Equipos y software para tu proceso académico.</dd></div>
          <div><Icon name="lightbulb" /><dt>Recursos</dt><dd>Herramientas para seguir aprendiendo.</dd></div>
        </dl>
      </div>
    </section>

    <section id="laboratorios" className="lis-surface-section lis-section home-anchor reveal-section" data-reveal>
      <div className="lis-container">
        <div className="lis-section-heading">
          <p className="lis-eyebrow">Nuestros laboratorios</p>
          <h2>Espacios equipados para tu aprendizaje</h2>
          <p>Contamos con equipos, sistemas operativos y software orientados a las necesidades académicas de los programas que usan nuestros espacios.</p>
        </div>
        <div className="laboratory-grid">
          {laboratories.map((lab, index) => (
            <article className="lis-card laboratory-card reveal-item" data-reveal style={{ transitionDelay: `${index * 65}ms` }} key={lab.name}>
              <span className="feature-icon"><Icon name={lab.icon} /></span>
              <h3>{lab.name}</h3>
              <p className="laboratory-location">{lab.location}</p>
              <p>{lab.description}</p>
              {lab.href ? <a className="text-link" href={lab.href} target="_blank" rel="noreferrer">Ver recursos <Arrow /></a> : <span className="laboratory-note">Espacio académico</span>}
            </article>
          ))}
        </div>
      </div>
    </section>

    <section id="comunidad" className="lis-container lis-section home-anchor reveal-section" data-reveal>
      <div className="lis-section-heading">
        <p className="lis-eyebrow">Comunidad</p>
        <h2>Aprende, comparte y participa</h2>
        <p>Publicamos noticias, eventos, concursos y datos de interés, y difundimos iniciativas del departamento como CODEX.</p>
      </div>
      <ul className="community-grid">
        {socialLinks.map((social, index) => (
          <li key={social.title}>
            <article className="lis-card community-card reveal-item" data-reveal style={{ transitionDelay: `${index * 55}ms` }}>
              <Icon name={social.id} className="community-icon" />
              <h3>{social.title}</h3>
              <p>{social.text}</p>
              <a className="text-link" href={social.href} target="_blank" rel="noreferrer">{social.cta} <Arrow /></a>
            </article>
          </li>
        ))}
      </ul>
    </section>

    <section id="recursos" className="lis-surface-section lis-section resources-section home-anchor reveal-section" data-reveal>
      <div className="lis-container">
        <div className="lis-section-heading">
          <p className="lis-eyebrow">Recursos educativos</p>
          <h2>Herramientas que apoyan tu formación</h2>
          <p>Los estudiantes de la Universidad de Antioquia pueden acceder a beneficios académicos y tecnológicos con su cuenta institucional.</p>
        </div>
        <div className="resources-grid">
          <article className="lis-card resource-card reveal-item" data-reveal>
            <p className="resource-label">GitHub</p><h3>Student Developer Pack</h3>
            <p>Herramientas y servicios de desarrollo, diseño y computación en la nube para estudiantes.</p>
            <a href="https://education.github.com/pack" target="_blank" rel="noreferrer" className="lis-button lis-button--primary">Conocer más <Arrow /></a>
          </article>
          <article className="lis-card resource-card reveal-item" data-reveal style={{ transitionDelay: '65ms' }}>
            <p className="resource-label">Microsoft</p><h3>Microsoft 365</h3>
            <p>Acceso institucional a Word, Excel, PowerPoint y otras aplicaciones de Office.</p>
            <a href="https://www.udea.edu.co/wps/portal/udea/web/generales/interna/!ut/p/z0/fYyxDoIwFEV_xYX5VWIQR0KMCWHSxEAX8wIPrJa-0hbj54sYBxe3e3JuDkioQBp8qB6DYoN65loml3SXx-tsI8rDschFluTZfns6l3EaQwHy_2EuqNs4ygxkwybQM0Bl2QXUU0sYCfS_dOWBvtvzwP4D1nFHnh35t1hKRrW8kFY-YMuR4K5TDa16h2FSgVc0WE2z8WDvsn4BDtonFg!!/" target="_blank" rel="noreferrer" className="lis-button lis-button--primary">Conocer más <Arrow /></a>
          </article>
        </div>
        <div className="assistants-cta lis-card reveal-section" data-reveal>
          <div><h3>¿Necesitas ayuda en el laboratorio?</h3><p>Nuestros auxiliares acompañan el funcionamiento de los espacios y están para ayudarte.</p></div>
          <Link to="/assistants" className="lis-button lis-button--secondary">Conoce a los auxiliares</Link>
        </div>
      </div>
    </section>
  </div>
  );
};

export default Home;
